
import { ChangeDetectionStrategy, Component, DestroyRef, Injector, Input, computed, forwardRef, input, signal, viewChild, type OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { sortBy } from "lodash";
import { ChecklistDirective } from "../../directives/checklist.directive";
import { tap } from "rxjs/operators";
import { ChecklistItemDirective } from "../../directives/checklist-item.directive";
import { TrimWhitespacePipe } from "../../pipes/trim-whitespace.pipe";

@Component({
	selector: "rl-checklist-control",
	templateUrl: "./checklist-control.component.html",
	styleUrl: "./checklist-control.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ChecklistControlComponent<KeyType>),
			multi: true
		}
	],
	imports: [ChecklistDirective, ReactiveFormsModule, ChecklistItemDirective, TrimWhitespacePipe]
})
export class ChecklistControlComponent<KeyType> implements OnInit, ControlValueAccessor {
	private readonly _injector = inject(Injector);
	private readonly _destroyRef = inject(DestroyRef);


	@Input({ required: true })
	formControl: FormControl<KeyType[]>;

	@Input()
	disableScroll = false;

	keyFn = input((r: unknown) => r as KeyType);
	labelFn = input((r: unknown) => r as string);

	/** have the selected items picked first */
	sortSelectedFirst = input(false);
	/** the list of options for the multi-select */
	options = input<unknown[]>([]);
	/** show the Select All option */
	showSelectAll = input<boolean>(true);

	rlDirective = viewChild<ChecklistDirective<KeyType>>("checklist");

	selectedValues = signal<KeyType[]>([]);

	kvps = computed<[KeyType, string][]>(() => {
		if (this.sortSelectedFirst()) {
			const currentSet = new Set<KeyType>(this.selectedValues());
			return sortBy(this.options(), o => !currentSet.has(this.keyFn()(o)))
				.map(o => [this.keyFn()(o), this.labelFn()(o)])
		} else {
			return this.options().map(o => [this.keyFn()(o), this.labelFn()(o)]);
		}
	});

	writeValue(obj: unknown): void {
		this.rlDirective().writeValue(obj);
	}
	registerOnChange(fn: unknown): void {
		this.rlDirective().registerOnChange(fn);
	}
	registerOnTouched(fn: unknown): void {
		this.rlDirective().registerOnTouched(fn);
	}
	setDisabledState?(isDisabled: boolean): void {
		this.rlDirective().setDisabledState(isDisabled);
	}

	ngOnInit(): void {

		this.formControl.valueChanges
			.pipe(
				tap((values) => {
					if (values) {
						this.selectedValues.set([...values]);
					} else {
						this.selectedValues.set(null);
					}
				}),
				takeUntilDestroyed(this._destroyRef))
			.subscribe();
	}

}
